import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { BehaviorSubject } from 'rxjs';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { isDefined } from '@fi-shared/utils';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { AuthenticateService } from '@fi-app/utilities/fi-services/authenticate.service';

@Component({
    selector: 'fi-profile-view',
    templateUrl: './profile-view.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./profile-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-profile-view' },
    standalone: false,
})
export class FiProfileViewComponent {
    @Input() profileName: string;
    @Input() profileNameSecondary: string;
    @Input() userId: number;
    @Input() profileLocked: boolean;
    @Input() lockedProfileLabel: string;
    @Input() editMenuItemLabel: string;
    @Input() deleteMenuItemLabel: string;
    @Input() hasEditAccess: boolean;
    @Input() hasDeleteAccess: boolean;
    @Output() editItem = new EventEmitter<void>();
    @Output() resetPassword = new EventEmitter<void>();
    @Output() deleteItem = new EventEmitter<void>();
    @Output() closeDialog: EventEmitter<void> = new EventEmitter<void>();

    isOpenConversationButtonShown$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    isResetPasswordButtonShown: boolean;
    isLockedInfoVisible: boolean;

    constructor(
        private fiChatService: FiChatService,
        private fiChatHelperService: FiChatHelperService,
        private userDataService: FiUserDataService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private fiSnackbarService: FiSnackBarService,
        private authenticateService: AuthenticateService,
    ) {}

    ngOnInit() {
        if (this.userId && this.fiChatHelperService.hasAccessToChat())
            this.fiChatService.isUserAvailableForConversation(this.userId).then((isAvailable: boolean) => {
                this.isOpenConversationButtonShown$.next(isAvailable);
            });

        this.isResetPasswordButtonShown = this.userId && !this.userDataService.isUserProfileMode(this.userId);
    }

    setIsLockedInfoVisible(isVisible: boolean) {
        this.isLockedInfoVisible = isVisible;
    }

    openConversation() {
        this.fiChatService.triggerOpenOneToOneConversationByUserId(this.userId);
        this.closeDialog.emit();
    }

    onCloseDialog() {
        this.closeDialog.emit();
    }

    onItemEdit() {
        this.editItem.emit();
    }

    onResetPasswordClick() {
        this.fiDialogsService
            .confirm({
                headerText: this.appTextService.get('user.reset_password'),
                messageText: this.appTextService.get('user.reset_password_dialog_message'),
                noButtonText: this.appTextService.get('common.cancel'),
                yesButtonText: this.appTextService.get('common.reset_password'),
                headerIconClass: 'fa-regular fa-arrow-rotate-right BrandedColor',
                isTopCloseButtonShown: true,
                config: {
                    panelClass: 'fi-rounded-dialog-panel',
                    restoreFocus: false,
                    autoFocus: false,
                },
            })
            .onYesButtonClick(() => {
                this.authenticateService.createResetPasswordLink(this.userId).then(() => {
                    this.fiSnackbarService.showSnackbar({
                        textMessage: this.appTextService.get('user.reset_password_sent_snackbar'),
                        buttonText: this.appTextService.get('common.dismiss'),
                    });
                });
            });
    }

    isDeleteOptionShown(): boolean {
        return isDefined(this.hasDeleteAccess) ? this.hasDeleteAccess : this.hasEditAccess;
    }

    onItemDelete() {
        this.deleteItem.emit();
    }
}
